import type { FC, KeyboardEvent } from "react";
import { createContext, use, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { CheckboxMark } from "@privacy/ui";
import { uniqueKeys } from "../../shared/uniqueKeys";

export type AssistantCardChoice = { label: string; description?: string };

type AssistantChoicesAnswer = { onSend?: (text: string) => void };

export const AssistantChoicesAnswerContext = createContext<AssistantChoicesAnswer>({});

const CARD_FRAME = {
  my: 0.5,
  maxWidth: 520,
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  overflow: "hidden",
} as const;

const ROW = {
  "width": "100%",
  "justifyContent": "flex-start",
  "textAlign": "left",
  "py": 2,
  "pr": 3,
  "gap": 1.5,
  "alignItems": "center",
  "&:hover": { bgcolor: "action.hover" },
  "&:focus-visible": { outline: "none", bgcolor: "action.hover" },
} as const;

const DIVIDED = { "borderTop": "1px solid", "borderColor": "divider", "&:first-of-type": { borderTop: 0 } } as const;

export const AssistantChoicesCardSkeleton: FC = () => (
  <Stack sx={CARD_FRAME} aria-hidden="true">
    {[70, 55, 62].map(width => (
      <Box key={width} sx={{ ...DIVIDED, px: 3, py: 2 }}>
        <Skeleton variant="text" width={`${width}%`} />
      </Box>
    ))}
  </Stack>
);

const ChoiceText: FC<{ label: string; description?: string }> = ({ label, description }) => (
  <Stack sx={{ gap: 0.25, flex: 1, minWidth: 0 }}>
    <Typography component="span" sx={{ fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.5 }}>
      {label}
    </Typography>
    {description && (
      <Typography
        component="span"
        sx={{ fontSize: theme => theme.typography.pxToRem(12), lineHeight: 1.5, color: "text.secondary" }}
      >
        {description}
      </Typography>
    )}
  </Stack>
);

export const AssistantChoicesCard: FC<{ choices: Array<AssistantCardChoice>; allowMultiple: boolean }> = ({
  choices,
  allowMultiple,
}) => {
  const { t } = useTranslation();
  const { onSend } = use(AssistantChoicesAnswerContext);
  const [chosen, setChosen] = useState<Array<number>>([]);
  const rowsRef = useRef<Array<HTMLButtonElement | null>>([]);

  if (!onSend) return null;

  const choiceKeys = uniqueKeys(choices.map(choice => choice.label));

  const pick = (index: number) => {
    if (!allowMultiple) return onSend(choices[index].label);
    setChosen(current => (current.includes(index) ? current.filter(item => item !== index) : [...current, index]));
  };

  const sendChosen = () => {
    const labels = choices.filter((_, index) => chosen.includes(index)).map(choice => choice.label);
    if (labels.length > 0) onSend(labels.join(", "));
  };

  const moveFocus = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const current = rowsRef.current.findIndex(row => row === document.activeElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next =
      current === -1 ? (step > 0 ? 0 : choices.length - 1) : (current + step + choices.length) % choices.length;
    rowsRef.current[next]?.focus();
  };

  return (
    <Stack onKeyDown={moveFocus} sx={CARD_FRAME}>
      <Box
        component="ul"
        role="group"
        aria-label={t("Assistant.Choices.GroupLabel")}
        sx={{ listStyle: "none", m: 0, p: 0 }}
      >
        {choices.map((choice, index) => {
          const isChosen = chosen.includes(index);
          return (
            <Box component="li" key={choiceKeys[index]} sx={DIVIDED}>
              <ButtonBase
                ref={element => {
                  rowsRef.current[index] = element;
                }}
                role={allowMultiple ? "checkbox" : undefined}
                aria-checked={allowMultiple ? isChosen : undefined}
                onClick={() => pick(index)}
                sx={{ ...ROW, pl: allowMultiple ? 1.5 : 3, bgcolor: isChosen ? "action.selected" : undefined }}
              >
                {allowMultiple && (
                  <Box sx={{ display: "inline-flex", p: 0.5 }}>
                    <CheckboxMark checked={isChosen} size="small" />
                  </Box>
                )}
                <ChoiceText label={choice.label} description={choice.description} />
              </ButtonBase>
            </Box>
          );
        })}
      </Box>

      {allowMultiple && (
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 3,
          }}
        >
          <Typography
            sx={{ fontSize: theme => theme.typography.pxToRem(12), lineHeight: 1.5, color: "text.secondary" }}
          >
            {t("Assistant.Choices.HintMultiple")}
          </Typography>
          <Button
            size="small"
            variant={chosen.length === 0 ? "outlined" : "contained"}
            disabled={chosen.length === 0}
            onClick={sendChosen}
          >
            {t("Assistant.Choices.Send")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
